'use client'

import { HugeiconsIcon } from '@hugeicons/react'
import { ArrowRight01Icon } from '@hugeicons-pro/core-stroke-rounded'
import { useChat } from '@/contexts/ChatContext'
import { useGate } from './gate/GateProvider'
import type { MarketplacePrompt } from '@/lib/marketplace/queries'

// Three states:
//   logged-out: prompt to sign in.
//   logged-in + zero prompts: prompt to save the first.
//   logged-in + has prompts: short summary + "save another" CTA.
// Listing prompts inline + chat handoff lands in a later PR.

export function PromptsPanel({ prompts }: { prompts: MarketplacePrompt[] }) {
  const { isLoggedIn, requireAuth } = useGate()
  const { openChat } = useChat()

  const copy = !isLoggedIn
    ? {
        title: 'Sign in to save prompts',
        body: 'Create an account to author custom Commands and run them like a built-in.',
        cta: 'Sign in',
      }
    : prompts.length === 0
      ? {
          title: 'Save your first Prompt',
          body: 'Capture a reusable instruction and run it like a built-in Command.',
          cta: 'Save a prompt',
        }
      : {
          title: `${prompts.length} prompt${prompts.length === 1 ? '' : 's'} saved`,
          body: 'Save another reusable instruction or run an existing one from chat.',
          cta: 'Save a prompt',
        }

  return (
    <div className="pb-[34px] pl-[var(--mp-pad-l)] pr-[var(--mp-pad-r)] pt-[26px]">
      <button
        type="button"
        onClick={() => {
          if (!isLoggedIn) {
            requireAuth()
            return
          }
          openChat(null)
        }}
        className="relative flex w-full flex-wrap items-center justify-between gap-5 overflow-hidden rounded-[18px] bg-[image:var(--band-brand-dark)] p-[34px] text-left transition-transform duration-200 ease-out hover:-translate-y-[2px] focus:outline-none focus-visible:ring-2 focus-visible:ring-success/60"
      >
        <span
          aria-hidden
          className="absolute -right-[60px] -top-[60px] h-[240px] w-[240px] rounded-full bg-success/15"
        />
        <div className="relative z-[2] min-w-0 flex-1">
          <div className="mb-2 font-mono text-xs uppercase tracking-[0.12em] text-success">
            Your prompts
          </div>
          <div className="mb-1.5 text-[28px] font-bold tracking-[-0.015em] text-white">
            {copy.title}
          </div>
          <div className="max-w-[430px] text-sm leading-[1.5] text-white/70">
            {copy.body}
          </div>
        </div>
        <span className="relative z-[2] inline-flex items-center gap-1.5 rounded-[10px] bg-success px-[22px] py-[13px] text-sm font-semibold text-brand-dark">
          {copy.cta}
          <HugeiconsIcon icon={ArrowRight01Icon} className="size-4" aria-hidden />
        </span>
      </button>
    </div>
  )
}
